import * as React from 'react';
import { TPhoneCasesList } from './PhoneCasesList.types';
import '../Products.styled.scss';
import { BreadcrumbsBlock } from 'components';
import { ProductCard } from 'components';
import { useAppDispatch, useAppSelector } from 'store/store';
import { getPhoneCasesListAsyncThunk } from 'store/phoneCases/phoneCases.action';
import { Loader } from 'components';
import { resetPhoneCasesList } from 'store/phoneCases/phoneCases.slices';
import { useFilters } from '../../../store/contexts/FiltersContext/FiltersContext.provider';
import { ProductsFiltersDrawer, ProductsFiltersButton } from 'components';
import { ProductsSortDropdown } from 'components';
import {
  PHONE_CASES_DEFAULT_QUERY_PARAMS,
  PHONE_CASES_FILTERS,
} from './PhoneCasesList.constants';
import { TProductFilters } from 'api/types';
import { NotFoundDisplay, ErrorDisplay } from 'components';
import { Button } from 'components';
import { TPhoneCasesGetListQueryParams } from 'api/phoneCases/phoneCases.types';
import { useUrlParams } from 'hooks/useUpdateUrlParams';
import { getTagsListAsyncThunk } from 'store/tags/tags.action';
import { resetTagsList } from 'store/tags/tags.slices';
import { tagListToFilterOptions } from 'utils/product';
import { useLocalize } from 'store/contexts/LocalizationContext/LocalizationContext.provider';

export const PhoneCasesList: TPhoneCasesList = () => {
  const localize = useLocalize();
  const dispatch = useAppDispatch();
  const { updateParams, getFiltersFromParams, getAllParams } = useUrlParams();

  const {
    data: phoneCasesList,
    isLoading: IsPhoneCasesLoading,
    error: phoneCasesError,
  } = useAppSelector((store) => store.phoneCases.phoneCasesList);

  const { data: tagsList, isLoading: IsTagsLoading } = useAppSelector(
    (store) => store.tags.tagsList
  );

  const { isFiltersOpen, closeFilters } = useFilters();
  const [filtersOpen, setFiltersOpen] = React.useState(false);

  const [selectedQueryParams, setSelectedQueryParams] =
    React.useState<TPhoneCasesGetListQueryParams>(
      PHONE_CASES_DEFAULT_QUERY_PARAMS
    );
  const [selectedSortBy, setSelectedSortBy] = React.useState(
    (getAllParams().orderBy as string) || 'default'
  );
  const [selectedFilters, setSelectedFilters] = React.useState<TProductFilters>(
    getFiltersFromParams() || {}
  );

  const loadPhoneCases = React.useCallback(
    (queryParams: TPhoneCasesGetListQueryParams) => {
      dispatch(
        getPhoneCasesListAsyncThunk({
          queryParams: { ...queryParams, isActive: true },
        })
      );
    },
    [dispatch]
  );

  // Sync local state with context
  React.useEffect(() => {
    setFiltersOpen(isFiltersOpen);
  }, [isFiltersOpen]);

  React.useEffect(() => {
    const splittedSortBy = selectedSortBy.split(':');
    console.log({ sorters: [{ [splittedSortBy[0]]: splittedSortBy[1] }] });
    loadPhoneCases({
      ...PHONE_CASES_DEFAULT_QUERY_PARAMS,
      ...selectedQueryParams,
      ...(selectedSortBy !== 'default'
        ? { sorters: { [splittedSortBy[0]]: splittedSortBy[1] } }
        : {}),
      filters: selectedFilters,
    });

    updateParams({
      filters: selectedFilters,
      orderBy: selectedSortBy === 'default' ? '' : selectedSortBy,
    });
  }, [selectedQueryParams, selectedFilters, selectedSortBy]);

  React.useEffect(() => {
    dispatch(getTagsListAsyncThunk({ queryParams: { isActive: true } }));
    return () => {
      dispatch(resetPhoneCasesList());
      dispatch(resetTagsList());
    };
  }, []);

  const shouldShowLoadMore = React.useMemo(() => {
    if (
      !phoneCasesList?.pagination ||
      IsPhoneCasesLoading ||
      IsTagsLoading ||
      phoneCasesError
    )
      return false;
    const { page, perPage, total } = phoneCasesList.pagination;
    return page * perPage < total;
  }, [phoneCasesList?.pagination]);

  const handleFiltersOpen = () => setFiltersOpen(true);

  const handleFiltersClose = () => {
    setFiltersOpen(false);
    closeFilters();
  };

  const handleLoadMore = React.useCallback(() => {
    setSelectedQueryParams({
      ...selectedQueryParams,
      ...selectedFilters,
      orderBy: selectedSortBy === 'default' ? '' : selectedSortBy,
      page: selectedQueryParams.page + 1,
    });
  }, [selectedQueryParams, selectedFilters, selectedSortBy]);

  const productFilters = React.useMemo(() => {
    return [
      ...PHONE_CASES_FILTERS,
      tagListToFilterOptions(tagsList?.items || []),
    ];
  }, [tagsList]);

  return (
    <div className="products-list">
      <div className="products-list__banner">
        <div className="products-list__banner__content">
          <BreadcrumbsBlock />
          <h2>{localize('products.list.title')}</h2>
          <p>{localize('products.list.subtitle')}</p>
        </div>
      </div>
      <div className="products-list__container">
        <div className="products-list__top-bar">
          <ProductsFiltersButton
            onClick={handleFiltersOpen}
            selectedFilters={selectedFilters}
            filters={PHONE_CASES_FILTERS}
          />

          <ProductsSortDropdown onSortChange={setSelectedSortBy} />
        </div>
        <div className="products-list__main-content">
          {IsPhoneCasesLoading || IsTagsLoading ? (
            <Loader />
          ) : (
            <>
              <div className="products-list__main-content__products">
                {phoneCasesList?.items?.map((phoneCaseItem) => (
                  <ProductCard
                    key={phoneCaseItem.id}
                    productData={phoneCaseItem}
                    localize={localize}
                  />
                ))}
              </div>
              {shouldShowLoadMore && (
                <div className="products-list__load-more">
                  <Button
                    variant="outlined"
                    onClick={handleLoadMore}
                    disabled={IsPhoneCasesLoading}
                    className="products-list__load-more__button"
                  >
                    {localize('products.load.more')}
                  </Button>
                </div>
              )}
              {phoneCasesList?.items?.length === 0 && !phoneCasesError && (
                <NotFoundDisplay />
              )}
              {phoneCasesError && <ErrorDisplay />}
            </>
          )}
        </div>
      </div>
      <ProductsFiltersDrawer
        open={filtersOpen}
        onClose={handleFiltersClose}
        filters={productFilters}
        setSelectedFilters={setSelectedFilters}
        selectedFilters={selectedFilters}
      />
    </div>
  );
};
